import { Component, DestroyRef, inject, Input, ViewChild } from '@angular/core';
import { PrismFileInfo, UploadComponent } from '../../../../shared/shared-components/upload/upload.component';
import { ModalAction } from '../../../../shared/shared-interfaces';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { PayersService } from '../../../payers/services/payers.service';
import { Router } from '@angular/router';
import { LoaderService } from '../../../../shared/shared-services/loader/loader.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

export const LOB_IMPORT = 'LOB_IMPORT';
export const LOB_ADDR_IMPORT = 'LOB_ADDRESS_IMPORT';

@Component({
  selector: 'prism-new-job-dialog',
  templateUrl: './new-job-dialog.component.html',
  styleUrls: ['./new-job-dialog.component.scss'],
})
export class NewJobDialogComponent {
  @Input({ required: true }) operationType: 'LOB_ADDRESS_IMPORT' | 'LOB_IMPORT';
  @ViewChild(UploadComponent) uploadComponent: UploadComponent;
  private destroyRef = inject(DestroyRef);
  public showDialog = false;
  public showErrorMessage = false;
  public detailedErrorMessage: string = null;
  public showDetailedError = false;
  public submitAction: ModalAction = {
    text: 'Submit',
    type: 'submit',
    disabled: true,
    callback: () => this.submit(),
  };
  public dialogActions: ModalAction[] = [
    {
      text: 'Cancel',
      type: 'fifth',
      callback: () => this.closeDialog(),
    },
    this.submitAction,
  ];
  @Input() fileRestrictions: FileRestrictions = {
    maxFileSize: 5000000,
    allowedExtensions: ['.csv'],
  };
  private fileToUpload: File = null;

  constructor(
    private payersService: PayersService,
    private router: Router,
    private loaderService: LoaderService,
  ) {}

  get dialogTitle(): string {
    if (this.operationType === LOB_ADDR_IMPORT) {
      return 'Address Import';
    } else if (this.operationType === LOB_IMPORT) {
      return 'LOB Import';
    }

    return '';
  }

  public show(): void {
    this.showErrorMessage = false;
    this.detailedErrorMessage = null;
    this.showDetailedError = false;
    this.uploadComponent.deleteAllDocuments();
    this.showDialog = true;
  }

  protected closeDialog(): void {
    this.showDialog = false;
  }

  private submit(): void {
    if (this.fileToUpload != null) {
      this.loaderService.setLoading(true);
      this.getOperation()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((jobId: string) => {
          this.navigateToScheduler(jobId);
        })
        .add(() => this.loaderService.setLoading(false));
    }
  }

  private getOperation(): Observable<string> {
    if (this.operationType === LOB_ADDR_IMPORT) {
      return this.payersService.importAddress(this.fileToUpload).pipe(map((resp) => resp.JobId));
    }
    if (this.operationType === LOB_IMPORT) {
      return this.payersService.importPayerData(this.fileToUpload).pipe(map((resp) => resp.jobId));
    }
    return of();
  }

  public selectedFile($event: Array<PrismFileInfo>): void {
    if ($event.length === 0) return;

    const rawFile = $event[0].rawFile;

    if (rawFile.name.endsWith('.csv')) {
      this.transformFileType(rawFile);
      return;
    }

    if (rawFile.name.endsWith('.xlsx')) {
      this.transformFileType(rawFile, 'application/octet-stream');
      return;
    }

    this.fileToUpload = rawFile;
    this.submitAction.disabled = false;
  }

  transformFileType(rawFile: File, mimeType = 'text/csv') {
    rawFile.arrayBuffer().then((arrayBuffer) => {
      const newBlob = new Blob([arrayBuffer], { type: mimeType });
      this.fileToUpload = new File([newBlob], rawFile.name, { type: mimeType });
      this.submitAction.disabled = false;
    });
  }

  private navigateToScheduler(jobId: string): void {
    this.router.navigate([`/admin/scheduler/jobs/list`], { queryParams: { wildcard: jobId } });
  }

  protected deletedFile(): void {
    this.submitAction.disabled = true;
  }

  protected showDetails(): void {
    this.showDetailedError = !this.showDetailedError;
  }
}
